import { tgpu, d, std } from 'typegpu';
import type { TgpuRoot, TgpuBuffer } from 'typegpu';
import type { StorageFlag } from 'typegpu';
import type { WgslArray } from 'typegpu/data';
import type { SimulationConfig } from '../../config/schema';
import { computeCorners, elasticPackA, elasticPackB } from '../../config/corners';
import { ResonantStateA, ResonantStateB, ResonantParams, ResonantDivParams, DataCell } from './types';
import { systemDeriv, computeResonantTip, resonantDivergence } from './deriv';
import { hash, samplePerturb } from '../shared/hash';
import { vizModeToNumber } from '../shared/constants';
import { DualStateSystem } from '../shared/DualStateSystem';
import type { GpuPipelinesResult, DivGpuPipelinesResult, NeighborPipelinesResult } from '../shared/DualStateSystem';
import { buildResonantNeighborPipelines } from './ResonantNeighbor';

export class ResonantSystem extends DualStateSystem<typeof ResonantStateA, typeof ResonantStateB, typeof ResonantParams, typeof ResonantDivParams, typeof DataCell> {
  readonly key = 'resonant' as const;
  readonly stateSize = 32;

  createStateArrayA(cellCount: number): WgslArray<typeof ResonantStateA> { return d.arrayOf(ResonantStateA, cellCount); }
  createStateArrayB(cellCount: number): WgslArray<typeof ResonantStateB> { return d.arrayOf(ResonantStateB, cellCount); }
  createDataArray(cellCount: number): WgslArray<typeof DataCell> { return d.arrayOf(DataCell, cellCount); }
  paramsStruct(): typeof ResonantParams { return ResonantParams; }
  divParamsStruct(): typeof ResonantDivParams { return ResonantDivParams; }
  zeroStateA(): Record<string, number>[] {
    return Array.from({ length: this.cellCount }, () => ({ theta0: 0, omega0: 0, dummy1: 0, dummy2: 0 }));
  }
  zeroStateB(): Record<string, number>[] {
    return Array.from({ length: this.cellCount }, () => ({ theta1: 0, omega1: 0, dummy1: 0, dummy2: 0 }));
  }

  protected buildGpuPipelines(root: TgpuRoot, stateAa: TgpuBuffer<WgslArray<typeof ResonantStateA>> & StorageFlag, stateAb: TgpuBuffer<WgslArray<typeof ResonantStateA>> & StorageFlag, stateBa: TgpuBuffer<WgslArray<typeof ResonantStateB>> & StorageFlag, stateBb: TgpuBuffer<WgslArray<typeof ResonantStateB>> & StorageFlag): GpuPipelinesResult {
    const initLayout = tgpu.bindGroupLayout({
      stateA: { storage: this.StateArrayA, access: 'mutable' },
      stateB: { storage: this.StateArrayB, access: 'mutable' },
      params: { uniform: ResonantParams },
    });
    const stepLayout = tgpu.bindGroupLayout({
      currentA: { storage: this.StateArrayA }, currentB: { storage: this.StateArrayB },
      nextA: { storage: this.StateArrayA, access: 'mutable' }, nextB: { storage: this.StateArrayB, access: 'mutable' },
      params: { uniform: ResonantParams },
    });
    const accumulateLayout = tgpu.bindGroupLayout({
      currentA: { storage: this.StateArrayA }, currentB: { storage: this.StateArrayB },
      data: { storage: this.DataArray, access: 'mutable' },
      params: { uniform: ResonantParams },
    });
    const il = initLayout; const sl = stepLayout; const al = accumulateLayout;

    const initCell = (ci: number) => {
      'use gpu';
      const p = il.$.params;
      const x = ci % d.u32(p.resolution); const y = ci / d.u32(p.resolution);
      const u = d.f32(x) / p.resolution; const v = d.f32(y) / p.resolution;
      const omu = 1 - u; const omv = 1 - v;
      il.$.stateA[ci] = ResonantStateA({ theta0: omu*omv*p.cA00_th+u*omv*p.cA10_th+omu*v*p.cA01_th+u*v*p.cA11_th, omega0: omu*omv*p.cA00_om+u*omv*p.cA10_om+omu*v*p.cA01_om+u*v*p.cA11_om, dummy1: 0, dummy2: 0 });
      il.$.stateB[ci] = ResonantStateB({ theta1: omu*omv*p.cB00_th+u*omv*p.cB10_th+omu*v*p.cB01_th+u*v*p.cB11_th, omega1: omu*omv*p.cB00_om+u*omv*p.cB10_om+omu*v*p.cB01_om+u*v*p.cB11_om, dummy1: 0, dummy2: 0 });
    };

    const stepCell = (ci: number) => {
      'use gpu';
      const sa = sl.$.currentA[ci]; const sb = sl.$.currentB[ci]; const p = sl.$.params;
      const t0 = sa.theta0; const w0 = sa.omega0;
      const t1 = sb.theta1; const w1 = sb.omega1;
      const dt = p.dt;
      const a0 = systemDeriv(t0, w0, t1, w1, p.rpM0, p.rpM1, p.rpL0, p.rpL1, p.rpA0);
      const hw0 = w0 + 0.5 * dt * a0.da_om;
      const hw1 = w1 + 0.5 * dt * a0.db_om;
      const nt0 = t0 + dt * hw0;
      const nt1 = t1 + dt * hw1;
      const a1 = systemDeriv(nt0, hw0, nt1, hw1, p.rpM0, p.rpM1, p.rpL0, p.rpL1, p.rpA0);
      sl.$.nextA[ci] = ResonantStateA({ theta0: nt0, omega0: hw0 + 0.5*dt*a1.da_om, dummy1: 0, dummy2: 0 });
      sl.$.nextB[ci] = ResonantStateB({ theta1: nt1, omega1: hw1 + 0.5*dt*a1.db_om, dummy1: 0, dummy2: 0 });
    };

    const accumulateCell = (ci: number) => {
      'use gpu';
      const sa = al.$.currentA[ci]; const sb = al.$.currentB[ci]; const data = al.$.data[ci]; const p = al.$.params;
      const vm = p.vizMode;
      if (vm < 0.5) {
        const tip = computeResonantTip(sa.theta0, sb.theta1, p.rpL0, p.rpL1, p.rpA0);
        if (data.a > 0.5) { const dx = tip.x - data.r; const dy = tip.y - data.g; const dist = std.sqrt(dx*dx+dy*dy); al.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: data.b+dist, a: 1 }); }
        else { al.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: 0, a: 1 }); }
      } else if (vm < 1.5) {
        const tip = computeResonantTip(sa.theta0, sb.theta1, p.rpL0, p.rpL1, p.rpA0);
        al.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: 0, a: 1 });
      } else if (vm < 3.5) {
        const tip = computeResonantTip(sa.theta0, sb.theta1, p.rpL0, p.rpL1, p.rpA0);
        if (data.a > 0.5) { const dx = tip.x - data.r; const dy = tip.y - data.g; const dist = std.sqrt(dx*dx+dy*dy); al.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: data.b+dist, a: 1 }); }
        else { al.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: 0, a: 1 }); }
      } else {
        const res = d.u32(p.resolution);
        const cx = ci % res; const cy = ci / res;
        const tip = computeResonantTip(sa.theta0, sb.theta1, p.rpL0, p.rpL1, p.rpA0);
        const nx0 = std.min(cx + 1, res - 1);
        const nx1 = std.max(d.i32(cx) - 1, 0);
        const ny0 = std.min(cy + 1, res - 1);
        const ny1 = std.max(d.i32(cy) - 1, 0);
        const sa0 = al.$.currentA[d.u32(ny0)*res+cx]; const sb0 = al.$.currentB[d.u32(ny0)*res+cx];
        const sa1 = al.$.currentA[d.u32(ny1)*res+cx]; const sb1 = al.$.currentB[d.u32(ny1)*res+cx];
        const sa2 = al.$.currentA[cy*res+nx0]; const sb2 = al.$.currentB[cy*res+nx0];
        const sa3 = al.$.currentA[cy*res+d.u32(nx1)]; const sb3 = al.$.currentB[cy*res+d.u32(nx1)];
        const t0 = computeResonantTip(sa0.theta0, sb0.theta1, p.rpL0, p.rpL1, p.rpA0);
        const t1 = computeResonantTip(sa1.theta0, sb1.theta1, p.rpL0, p.rpL1, p.rpA0);
        const t2 = computeResonantTip(sa2.theta0, sb2.theta1, p.rpL0, p.rpL1, p.rpA0);
        const t3 = computeResonantTip(sa3.theta0, sb3.theta1, p.rpL0, p.rpL1, p.rpA0);
        const avgDx = (tip.x-t0.x + tip.x-t1.x + tip.x-t2.x + tip.x-t3.x) * 0.25;
        const avgDy = (tip.y-t0.y + tip.y-t1.y + tip.y-t2.y + tip.y-t3.y) * 0.25;
        const avgDist = std.sqrt(avgDx*avgDx + avgDy*avgDy);
        if (vm < 4.5) {
          al.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: avgDist, a: 1 });
        } else {
          if (data.a > 0.5) {
            al.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: data.b + avgDist, a: 1 });
          } else {
            al.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: avgDist, a: 1 });
          }
        }
      }
    };

    return {
      initLayout, stepLayout, accumulateLayout,
      initPipeline: root.createGuardedComputePipeline(initCell),
      stepPipeline: root.createGuardedComputePipeline(stepCell),
      accumulatePipeline: root.createGuardedComputePipeline(accumulateCell),
      initBG: root.createBindGroup(initLayout, { stateA: stateAa, stateB: stateBa, params: this.paramsBuffer }),
      stepFwd: root.createBindGroup(stepLayout, { currentA: stateAa, currentB: stateBa, nextA: stateAb, nextB: stateBb, params: this.paramsBuffer }),
      stepBwd: root.createBindGroup(stepLayout, { currentA: stateAb, currentB: stateBb, nextA: stateAa, nextB: stateBa, params: this.paramsBuffer }),
      accA: root.createBindGroup(accumulateLayout, { currentA: stateAa, currentB: stateBa, data: this.dataBuf, params: this.paramsBuffer }),
      accB: root.createBindGroup(accumulateLayout, { currentA: stateAb, currentB: stateBb, data: this.dataBuf, params: this.paramsBuffer }),
    };
  }

  protected buildDivGpuPipelines(root: TgpuRoot, stateAa: TgpuBuffer<WgslArray<typeof ResonantStateA>> & StorageFlag, stateAb: TgpuBuffer<WgslArray<typeof ResonantStateA>> & StorageFlag, stateBa: TgpuBuffer<WgslArray<typeof ResonantStateB>> & StorageFlag, stateBb: TgpuBuffer<WgslArray<typeof ResonantStateB>> & StorageFlag): DivGpuPipelinesResult {
    const divInitLayout = tgpu.bindGroupLayout({
      baseStateA: { storage: this.StateArrayA, access: 'mutable' }, baseStateB: { storage: this.StateArrayB, access: 'mutable' },
      pertStateA: { storage: this.StateArrayA, access: 'mutable' }, pertStateB: { storage: this.StateArrayB, access: 'mutable' },
      divData: { storage: this.DataArray, access: 'mutable' }, params: { uniform: ResonantDivParams },
    });
    const divStepLayout = tgpu.bindGroupLayout({
      baseCurrentA: { storage: this.StateArrayA }, baseCurrentB: { storage: this.StateArrayB },
      pertCurrentA: { storage: this.StateArrayA }, pertCurrentB: { storage: this.StateArrayB },
      baseNextA: { storage: this.StateArrayA, access: 'mutable' }, baseNextB: { storage: this.StateArrayB, access: 'mutable' },
      pertNextA: { storage: this.StateArrayA, access: 'mutable' }, pertNextB: { storage: this.StateArrayB, access: 'mutable' },
      divData: { storage: this.DataArray, access: 'mutable' }, params: { uniform: ResonantDivParams },
    });
    const dil = divInitLayout; const dsl = divStepLayout;

    const verletAdvance = (t0: number, w0: number, t1: number, w1: number, m0: number, m1: number, L0: number, L1: number, a0: number, dt: number) => {
      'use gpu';
      const d0 = systemDeriv(t0, w0, t1, w1, m0, m1, L0, L1, a0);
      const hw0 = w0 + 0.5 * dt * d0.da_om;
      const hw1 = w1 + 0.5 * dt * d0.db_om;
      const nt0 = t0 + dt * hw0;
      const nt1 = t1 + dt * hw1;
      const d1 = systemDeriv(nt0, hw0, nt1, hw1, m0, m1, L0, L1, a0);
      return { th0: nt0, om0: hw0 + 0.5*dt*d1.da_om, th1: nt1, om1: hw1 + 0.5*dt*d1.db_om };
    };

    const divInitCell = (ci: number) => {
      'use gpu';
      const p = dil.$.params;
      const x = ci % d.u32(p.resolution); const y = ci / d.u32(p.resolution);
      const u = d.f32(x) / p.resolution; const v = d.f32(y) / p.resolution;
      const omu = 1 - u; const omv = 1 - v;
      const th0 = omu*omv*p.cA00_th+u*omv*p.cA10_th+omu*v*p.cA01_th+u*v*p.cA11_th;
      const om0 = omu*omv*p.cA00_om+u*omv*p.cA10_om+omu*v*p.cA01_om+u*v*p.cA11_om;
      const th1 = omu*omv*p.cB00_th+u*omv*p.cB10_th+omu*v*p.cB01_th+u*v*p.cB11_th;
      const om1 = omu*omv*p.cB00_om+u*omv*p.cB10_om+omu*v*p.cB01_om+u*v*p.cB11_om;
      dil.$.baseStateA[ci] = ResonantStateA({ theta0: th0, omega0: om0, dummy1: 0, dummy2: 0 });
      dil.$.baseStateB[ci] = ResonantStateB({ theta1: th1, omega1: om1, dummy1: 0, dummy2: 0 });
      const px = d.f32(x) / p.resolution; const py = d.f32(y) / p.resolution;
      const h0 = samplePerturb(px*1000+p.seed, py*1000+p.seed, p.perturb, p.perturbMode);
      const h1 = samplePerturb(px*1000+100+p.seed, py*1000+p.seed, p.perturb, p.perturbMode);
      dil.$.pertStateA[ci] = ResonantStateA({ theta0: th0+h0, omega0: om0, dummy1: 0, dummy2: 0 });
      dil.$.pertStateB[ci] = ResonantStateB({ theta1: th1+h1, omega1: om1, dummy1: 0, dummy2: 0 });
      dil.$.divData[ci] = DataCell({ r: 0, g: 0, b: 0, a: 1 });
    };

    const divStepCell = (ci: number) => {
      'use gpu';
      const p = dsl.$.params;
      const bsa = dsl.$.baseCurrentA[ci]; const bsb = dsl.$.baseCurrentB[ci];
      const psa = dsl.$.pertCurrentA[ci]; const psb = dsl.$.pertCurrentB[ci];
      const bn = verletAdvance(bsa.theta0, bsa.omega0, bsb.theta1, bsb.omega1, p.rpM0, p.rpM1, p.rpL0, p.rpL1, p.rpA0, p.dt);
      dsl.$.baseNextA[ci] = ResonantStateA({ theta0: bn.th0, omega0: bn.om0, dummy1: 0, dummy2: 0 });
      dsl.$.baseNextB[ci] = ResonantStateB({ theta1: bn.th1, omega1: bn.om1, dummy1: 0, dummy2: 0 });
      const pn = verletAdvance(psa.theta0, psa.omega0, psb.theta1, psb.omega1, p.rpM0, p.rpM1, p.rpL0, p.rpL1, p.rpA0, p.dt);
      dsl.$.pertNextA[ci] = ResonantStateA({ theta0: pn.th0, omega0: pn.om0, dummy1: 0, dummy2: 0 });
      dsl.$.pertNextB[ci] = ResonantStateB({ theta1: pn.th1, omega1: pn.om1, dummy1: 0, dummy2: 0 });
      const div = resonantDivergence(bn.th0, bn.om0, bn.th1, bn.om1, pn.th0, pn.om0, pn.th1, pn.om1);
      const data = dsl.$.divData[ci];
      if (data.g < 0.5) {
        if (div > 1.0) { dsl.$.divData[ci] = DataCell({ r: p.frameCounter, g: 1, b: div, a: 1 }); }
      } else {
        dsl.$.divData[ci] = DataCell({ r: data.r, g: 1, b: div, a: 1 });
      }
    };

    return {
      divInitLayout, divStepLayout,
      divInitPipeline: root.createGuardedComputePipeline(divInitCell),
      divStepPipeline: root.createGuardedComputePipeline(divStepCell),
      divInitBG: root.createBindGroup(divInitLayout, { baseStateA: stateAa, baseStateB: stateBa, pertStateA: this.pertStateAa, pertStateB: this.pertStateBa, divData: this.divDataBuf, params: this.divParamsBuffer }),
      divStepFwd: root.createBindGroup(divStepLayout, { baseCurrentA: stateAa, baseCurrentB: stateBa, pertCurrentA: this.pertStateAa, pertCurrentB: this.pertStateBa, baseNextA: stateAb, baseNextB: stateBb, pertNextA: this.pertStateAb, pertNextB: this.pertStateBb, divData: this.divDataBuf, params: this.divParamsBuffer }),
      divStepBwd: root.createBindGroup(divStepLayout, { baseCurrentA: stateAb, baseCurrentB: stateBb, pertCurrentA: this.pertStateAb, pertCurrentB: this.pertStateBb, baseNextA: stateAa, baseNextB: stateBa, pertNextA: this.pertStateAa, pertNextB: this.pertStateBa, divData: this.divDataBuf, params: this.divParamsBuffer }),
    };
  }

  buildParamsData(config: SimulationConfig): Record<string, number> {
    this.lastConfig = config;
    return packRpParams(config);
  }

  buildDivParamsData(config: SimulationConfig, seed: number, perturb: number): Record<string, number> & { frameCounter: number } {
    return { ...packRpParams(config), seed, perturb, perturbMode: config.perturbDistribution === 'gaussian' ? 1 : 0, frameCounter: 0 };
  }

  protected buildClosenessOfReturnPipeline(
    root: TgpuRoot, stateAa: TgpuBuffer<WgslArray<typeof ResonantStateA>> & StorageFlag, stateAb: TgpuBuffer<WgslArray<typeof ResonantStateA>> & StorageFlag,
    stateBa: TgpuBuffer<WgslArray<typeof ResonantStateB>> & StorageFlag, stateBb: TgpuBuffer<WgslArray<typeof ResonantStateB>> & StorageFlag,
    snapA: TgpuBuffer<WgslArray<typeof ResonantStateA>> & StorageFlag, snapB: TgpuBuffer<WgslArray<typeof ResonantStateB>> & StorageFlag,
    dataBuf: TgpuBuffer<WgslArray<typeof DataCell>> & StorageFlag,
  ): import('../shared/DualStateSystem').CORPipelineResult {
    const TTRU = d.struct({ currentIter: d.f32, minImprovement: d.f32, _p0: d.f32, _p1: d.f32 });
    const ttrLayout = tgpu.bindGroupLayout({
      curA: { storage: this.StateArrayA }, curB: { storage: this.StateArrayB },
      nextA: { storage: this.StateArrayA, access: 'mutable' }, nextB: { storage: this.StateArrayB, access: 'mutable' },
      snapA: { storage: this.StateArrayA }, snapB: { storage: this.StateArrayB },
      data: { storage: this.DataArray, access: 'mutable' }, params: { uniform: ResonantParams }, ttr: { uniform: TTRU },
    });
    const tl = ttrLayout;
    const verlet = (t0: number, w0: number, t1: number, w1: number, m0: number, m1: number, L0: number, L1: number, a0: number, dt: number) => {
      'use gpu';
      const d0 = systemDeriv(t0, w0, t1, w1, m0, m1, L0, L1, a0);
      const hw0 = w0 + 0.5*dt*d0.da_om; const hw1 = w1 + 0.5*dt*d0.db_om;
      const nt0 = t0 + dt*hw0; const nt1 = t1 + dt*hw1;
      const d1 = systemDeriv(nt0, hw0, nt1, hw1, m0, m1, L0, L1, a0);
      return { t0: nt0, om0: hw0+0.5*dt*d1.da_om, t1: nt1, om1: hw1+0.5*dt*d1.db_om };
    };
    const TWO_PI = 6.28318530718;
    const cd = (a: number) => { 'use gpu'; return a - std.floor(a / TWO_PI + 0.5) * TWO_PI; };
    const ttrCell = (ci: number) => {
      'use gpu';
      const cA = tl.$.curA[ci]; const cB = tl.$.curB[ci]; const p = tl.$.params;
      const n = verlet(cA.theta0, cA.omega0, cB.theta1, cB.omega1, p.rpM0, p.rpM1, p.rpL0, p.rpL1, p.rpA0, p.dt);
      tl.$.nextA[ci] = ResonantStateA({ theta0: n.t0, omega0: n.om0, dummy1: 0, dummy2: 0 });
      tl.$.nextB[ci] = ResonantStateB({ theta1: n.t1, omega1: n.om1, dummy1: 0, dummy2: 0 });
      const snA = tl.$.snapA[ci]; const snB = tl.$.snapB[ci];
      const d0=cd(n.t0-snA.theta0); const dw0=n.om0-snA.omega0; const d1=cd(n.t1-snB.theta1); const dw1=n.om1-snB.omega1;
      const dist = std.sqrt(d0*d0+dw0*dw0+d1*d1+dw1*dw1);
      const data = tl.$.data[ci]; const ttr = tl.$.ttr;
      if (data.a < 0.5) { tl.$.data[ci] = DataCell({ r: ttr.currentIter, g: 0, b: dist, a: 1 }); }
      else if (dist < data.b - ttr.minImprovement) { tl.$.data[ci] = DataCell({ r: ttr.currentIter, g: data.g, b: dist, a: data.a }); }
    };
    return { ttrPipeline: root.createGuardedComputePipeline(ttrCell), ttrFwd: root.createBindGroup(ttrLayout, { curA: stateAa as never, curB: stateBa as never, nextA: stateAb as never, nextB: stateBb as never, snapA: snapA as never, snapB: snapB as never, data: dataBuf as never, params: this.paramsBuffer as never, ttr: this.ttrUniformsBuf as never }), ttrBwd: root.createBindGroup(ttrLayout, { curA: stateAb as never, curB: stateBb as never, nextA: stateAa as never, nextB: stateBa as never, snapA: snapA as never, snapB: snapB as never, data: dataBuf as never, params: this.paramsBuffer as never, ttr: this.ttrUniformsBuf as never }) };
  }

  protected buildNeighborPipelines(
    root: TgpuRoot,
    stateAa: TgpuBuffer<WgslArray<typeof ResonantStateA>> & StorageFlag, stateAb: TgpuBuffer<WgslArray<typeof ResonantStateA>> & StorageFlag,
    stateBa: TgpuBuffer<WgslArray<typeof ResonantStateB>> & StorageFlag, stateBb: TgpuBuffer<WgslArray<typeof ResonantStateB>> & StorageFlag,
  ): NeighborPipelinesResult {
    return buildResonantNeighborPipelines({
      root,
      StateArrayA: this.StateArrayA, StateArrayB: this.StateArrayB, DataArray: this.DataArray,
      NbStackArrayA: this.nbStackArrayA, NbStackArrayB: this.nbStackArrayB,
      paramsBuffer: this.paramsBuffer, nbParamsBuffer: this.nbParamsBuffer,
      baseAa: stateAa, baseAb: stateAb, baseBa: stateBa, baseBb: stateBb,
      nbPertAa: this.nbPertAa, nbPertAb: this.nbPertAb, nbPertBa: this.nbPertBa, nbPertBb: this.nbPertBb,
      nbDataBuf: this.nbDataBuf,
    });
  }
}

function packRpParams(config: SimulationConfig): Record<string, number> {
  const corners = computeCorners(config);
  const ca = (i: number) => elasticPackA(corners[i]);
  const cb = (i: number) => elasticPackB(corners[i]);
  return {
    rpM0: config.rpM0, rpM1: config.rpM1, rpL0: config.rpL0, rpL1: config.rpL1, rpA0: config.rpA0,
    dt: config.dt, resolution: config.resolution,
    vizMode: vizModeToNumber(config.vizMode),
    cA00_th: ca(0)[0], cA00_om: ca(0)[1], cA00_d1: ca(0)[2], cA00_d2: ca(0)[3],
    cA10_th: ca(1)[0], cA10_om: ca(1)[1], cA10_d1: ca(1)[2], cA10_d2: ca(1)[3],
    cA01_th: ca(2)[0], cA01_om: ca(2)[1], cA01_d1: ca(2)[2], cA01_d2: ca(2)[3],
    cA11_th: ca(3)[0], cA11_om: ca(3)[1], cA11_d1: ca(3)[2], cA11_d2: ca(3)[3],
    cB00_th: cb(0)[0], cB00_om: cb(0)[1], cB00_d1: cb(0)[2], cB00_d2: cb(0)[3],
    cB10_th: cb(1)[0], cB10_om: cb(1)[1], cB10_d1: cb(1)[2], cB10_d2: cb(1)[3],
    cB01_th: cb(2)[0], cB01_om: cb(2)[1], cB01_d1: cb(2)[2], cB01_d2: cb(2)[3],
    cB11_th: cb(3)[0], cB11_om: cb(3)[1], cB11_d1: cb(3)[2], cB11_d2: cb(3)[3],
  };
}